<script setup lang="ts">
import { ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { AnimatePresence, motion } from 'motion-v';

import type { AppShellSidebarNavTarget, SidebarItem } from '@apexx/component-library';
import { AppShellSidebar, AppShellSidebarNav } from '@apexx/component-library';

withDefaults(
	defineProps<{
		isEmbedded: boolean;
		mobileItems?: SidebarItem[];
		activeRoute?: string;
		showAppSwitcher: boolean;
	}>(),
	{
		mobileItems: () => [],
	},
);

const emit = defineEmits<{
	navigate: [to: AppShellSidebarNavTarget];
	switchApps: [];
	openSettings: [];
}>();

const mobileSidebarOpen = ref(false);
const route = useRoute();

function openMobileSidebar(): void {
	mobileSidebarOpen.value = true;
}

function closeMobileSidebar(): void {
	mobileSidebarOpen.value = false;
}

function navigate(to: AppShellSidebarNavTarget): void {
	emit('navigate', to);
	closeMobileSidebar();
}

function openSettings(): void {
	closeMobileSidebar();
	emit('openSettings');
}

watch(
	() => route.fullPath,
	() => {
		closeMobileSidebar();
	},
);

defineExpose({
	mobileSidebarOpen,
	openMobileSidebar,
	closeMobileSidebar,
});
</script>

<template>
	<div
		:class="[
			'flex min-h-0 w-full min-w-0 overflow-hidden bg-surface',
			isEmbedded ? 'h-full' : 'h-dvh',
		]"
	>
		<AnimatePresence>
			<motion.div
				v-if="mobileSidebarOpen"
				key="mobile-sidebar"
				class="fixed inset-0 z-50 flex lg:hidden"
				:initial="{ x: '-100%' }"
				:animate="{ x: 0 }"
				:exit="{ x: '-100%' }"
				:transition="{ duration: 0.3 }"
			>
				<slot
					name="mobile-sidebar"
					:navigate="navigate"
					:close="closeMobileSidebar"
					:show-app-switcher="showAppSwitcher"
					:switch-apps="() => emit('switchApps')"
					:open-settings="openSettings"
				>
					<div class="h-full w-full bg-surface shadow-xl pr-(--safe-area-right)">
						<AppShellSidebar
							variant="mobile"
							:show-app-switcher="showAppSwitcher"
							@switch-apps="emit('switchApps')"
							@close="closeMobileSidebar"
						>
							<template #logo>
								<slot name="sidebar-title" />
							</template>
							<slot
								name="mobile-sidebar-nav"
								:navigate="navigate"
								:close="closeMobileSidebar"
							>
								<AppShellSidebarNav
									:items="mobileItems"
									:active-route="activeRoute"
									variant="mobile"
									@navigate="navigate"
								/>
							</slot>
						</AppShellSidebar>
					</div>
				</slot>
			</motion.div>
		</AnimatePresence>

		<div
			:class="[
				'hidden shrink-0 overflow-y-auto lg:block',
				isEmbedded ? 'h-full max-h-full' : 'max-h-screen',
			]"
		>
			<slot
				name="desktop-sidebar"
				:navigate="navigate"
				:show-app-switcher="showAppSwitcher"
				:switch-apps="() => emit('switchApps')"
				:open-settings="openSettings"
			/>
		</div>

		<div class="flex min-h-0 min-w-0 w-full flex-1 flex-col">
			<slot :open-mobile-sidebar="openMobileSidebar" :close-mobile-sidebar="closeMobileSidebar" />
		</div>
	</div>
</template>
